import type {
  ExpandState,
  ExpanderHandle,
} from 'components/Containers/Expander';
import type { ReactNode } from 'react';

import css from '@emotion/css';
import styled from '@emotion/styled';
import { usePermissionValue } from '@kizen/permissions/hooks/usePermissionValue';
import { usePermissionSetter } from '@kizen/permissions/hooks/usePermissionSetter';
import { borderRadii, layers } from 'app/spacing';
import { useCallback, useRef, useState } from 'react';
import Expander from 'components/Containers/Expander';
import KizenLoader from '__components/Kizen/Loader';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import KizenSwitch from '__components/Kizen/Switch';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';

type SectionProps = {
  title: string;
  sectionId: string;
  children: ReactNode;
  onChange(value: boolean): void;
  onExpand?(): void;
  onExpanderHover?(): void;
  loading?: boolean;
  showExpander?: boolean;
  showArrowBtn?: boolean;
  initialExpanderState?: ExpandState;
  showSwitchToggle?: boolean;
  disableMargin?: boolean;
};

const Wrapper = styled.div<{ disableMargin?: boolean }>`
  position: relative;
  border: 1px solid var(--color-border-medium);
  border-radius: ${borderRadii.small};
  ${({ disableMargin }) =>
    disableMargin !== true &&
    css`
      margin-bottom: 15px;
      margin-top: 20px;
    `}
  table {
    width: calc(100% + 3px);
    margin-right: -7px;
  }
`;

const Header = styled.div`
  height: 34px;
  background-color: var(--color-background-default);
  border-top-left-radius: ${borderRadii.small};
  border-top-right-radius: ${borderRadii.small};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  z-index: ${layers.content(0, 2)}; // Appear on top of the gradient
  position: relative;
`;

const Switch = styled(KizenSwitch)`
  margin: 0 0 0 5px;
  label {
    margin: 0;
  }
`;

const ContentWrapper = styled.div`
  padding: 20px;
`;

const FittedGradient = styled(Gradient)`
  // match to rounding of bottom of Wrapper
  > :first-child {
    border-bottom-left-radius: ${borderRadii.small};
    border-bottom-right-radius: ${borderRadii.small};
  }
`;

const Loader = styled(KizenLoader)`
  padding-top: 5px !important;
`;

export const Section = ({
  title,
  sectionId,
  children,
  onChange,
  onExpand,
  onExpanderHover,
  loading = false,
  showExpander = true,
  showArrowBtn = true,
  initialExpanderState = 'partially-open',
  showSwitchToggle = true,
  disableMargin = false,
}: SectionProps) => {
  const expanderHandle = useRef<ExpanderHandle>(null);
  const [isOpen, setIsOpen] = useState(false);
  const setPermission = usePermissionSetter(sectionId);
  const show = usePermissionValue(sectionId, ({ value }) => Boolean(value));
  const showSwitch = usePermissionValue(
    sectionId,
    ({ allowed_access }) => allowed_access?.length > 1
  );

  const handleExpanderChange = useCallback(
    (newState: ExpandState) => {
      const open = newState === 'open';

      setIsOpen(open);

      if (open) {
        onExpand?.();
      }
    },
    [onExpand]
  );

  return (
    <Wrapper
      data-qa-permission-section={sectionId}
      disableMargin={disableMargin}
    >
      <Header>
        <TextEllipsisWithTooltip as="h3">{title}</TextEllipsisWithTooltip>
        {showSwitchToggle && showSwitch && (
          <Switch
            data-qa-permission-section-toggle
            textPlacement="none"
            checked={show}
            onChange={(ev) => {
              const { checked } = ev.target;

              if (showExpander && expanderHandle.current) {
                if (checked) {
                  expanderHandle.current.set('partially-open');
                } else {
                  expanderHandle.current.set('close');
                }
              }

              setPermission(checked);
              onChange?.(checked);
            }}
          />
        )}
      </Header>
      {showExpander && (
        <Expander
          ref={expanderHandle}
          title={title}
          initialState={show ? initialExpanderState : 'close'}
          closedState={show ? 'partially-open' : 'close'}
          gradient={<FittedGradient />}
          partiallyOpenHeight={50}
          showArrowBtn={showArrowBtn && show}
          onExpanderHover={onExpanderHover}
          onChange={handleExpanderChange}
        >
          {loading && isOpen ? (
            <Loader loading />
          ) : (
            <ContentWrapper>{children}</ContentWrapper>
          )}
        </Expander>
      )}
    </Wrapper>
  );
};
